import { createContext, useContext, useEffect, useRef, useState } from "react";

import { SimpleAudioState } from "../Audio/SimpleAudio";
import { useMentionCard } from "./context";
import EpisodeAudio from "./partials/EpisodeAudio";

// Define the type for local audio context, containing audio of an episode
type LocalAudioContextType = { audio: EpisodeAudio };

// Create a local audio context using the LocalAudioContextType
const LocalAudioContext = createContext<LocalAudioContextType>(
  {} as LocalAudioContextType
);

// Define the type for audio subscriptions, indicating the parts of the audio state we're subscribed to
type AudioSubscriptions = Partial<{ [K in keyof SimpleAudioState]: boolean }>;

// Define the type for audio state, containing the subscribed parts of the audio state
type AudioState<S extends AudioSubscriptions> = Partial<{
  [K in keyof S]: K extends keyof SimpleAudioState
    ? SimpleAudioState[K]
    : never;
}>;

/**
 * useLocalAudio
 * @description Provides the audio object and state for the current episode context.
 * @param subscriptions List of subscriptions to listen to. Defaults to none.
 * @returns Audio state and the audio object.
 */

export const useLocalAudio = <T extends AudioSubscriptions>(
  subscriptions: T = {} as T
) => {
  const { audio } = useContext(LocalAudioContext);
  const [state, setState] = useState<AudioState<T>>(() => {
    const keys = Object.keys(subscriptions);
    const s = keys.reduce(
      (initial, key) => ({ ...initial, [key]: undefined }),
      {}
    );
    return s;
  });

  const subs = useRef(
    Object.entries(subscriptions)
      .filter(([_k, v]) => v)
      .map(([k]) => k)
  );

  useEffect(() => {
    if (!audio || subs.current.length === 0)
      return () => {
        // Do nothing.
      };
    const handleStateChange = (audioState: AudioState<T>) => {
      const newState: AudioState<T> = {};
      subs.current.forEach((sub) => {
        (newState[sub as keyof T] as T[keyof T]) = audioState[
          sub as keyof T
        ] as T[keyof T];
      });
      setState(newState);
    };

    audio?.on("*", handleStateChange);

    return () => {
      audio?.off("*", handleStateChange);
    };
  }, [audio]);

  return { ...state, audio };
};

export const LocalAudioProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const mentionCard = useMentionCard();
  const audio = useRef<EpisodeAudio | null>(null);

  useEffect(() => {
    if (mentionCard?.episode.audio_url && !audio.current) {
      audio.current = new EpisodeAudio(mentionCard);
    }
  }, [mentionCard]);

  return (
    <LocalAudioContext.Provider
      value={{
        audio: audio.current!,
      }}
    >
      {children}
    </LocalAudioContext.Provider>
  );
};
